import './global-initialize';
import 'bootstrap';
import './main.scss';
import './prototypes/array';

import { library, dom } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';

library.add(fas, far, fab);
// Kicks off the process of finding <i> tags and replacing with <svg>
dom.watch();

import Aurelia from 'aurelia';
import { RouterConfiguration } from '@aurelia/router';
import { I18nConfiguration } from '@aurelia/i18n';
import { ValidationHtmlConfiguration, ValidationTrigger } from '@aurelia/validation-html';
import { HttpClient, IHttpClient } from '@aurelia/fetch-client';

import { Translations } from './locales/translations';
import { ConverterRegistrations } from './converters';
import { ComponentRegistrations } from './components';
import { Shell } from './shell';
import { UserApi } from './apis/user/user-api';
import { UserContext } from './contexts/user-context';

import { LanguageHelper } from './infrastructure/language-helper';
import { ApiBase } from './apis/api-base';
import { ProjectDraftStorageService } from './services/project-draft-storage-service';
import { AuthorizationHook } from './routing/authorization-hook';

// Cookie takes priority; Accept-Language header is the server-side equivalent but
// navigator.language is the client-side proxy for first-time visitors.
function resolveLanguage(): string {
  const pathLanguage = LanguageHelper.resolveLanguageFromPath(window.location.pathname);
  if (pathLanguage) return pathLanguage;

  const cookieLanguage = LanguageHelper.resolveLanguageFromCookie(document.cookie);
  if (cookieLanguage) return cookieLanguage;

  const browserLang = (navigator.language ?? '').split('-')[0].toLowerCase();
  return browserLang === 'en' ? 'en' : 'sv';
}
const culture = resolveLanguage();

const au = new Aurelia();

au.container.register(
  RouterConfiguration.customize({ useUrlFragmentHash: false }),
  AuthorizationHook,
  ValidationHtmlConfiguration.customize((options) => {
    options.DefaultTrigger = ValidationTrigger.blur;
  }),
  I18nConfiguration.customize((options) => {
    options.translationAttributeAliases = ['t', 'i18n'];
    options.initOptions = {
      lng: culture,
      fallbackLng: 'sv',
      debug: false,
      ns: ['errors', 'ui'],
      resources: Translations,
    };
  }),
  HttpClient,
  ...ConverterRegistrations,
  ...ComponentRegistrations,
);

const httpClient = au.container.get(IHttpClient);
httpClient.configure((config) =>
  config
    // Anchor API calls to the site root so they are unaffected by the dynamic
    // <base href> (which scopes the router to /<culture>/app/). Without this,
    // relative 'api/...' URLs would resolve to /<culture>/app/api/... and 404.
    .withBaseUrl(`${window.location.origin}/`)
    .withDefaults({
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
);

async function initializeCsrfToken(): Promise<void> {
  const response = await fetch(`${window.location.origin}/api/security/csrf`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to initialize CSRF token (status ${response.status}).`);
  }

  const payload = await response.json() as { requestToken?: string };
  if (!payload.requestToken) {
    throw new Error('CSRF token endpoint did not return requestToken.');
  }

  ApiBase.setCsrfToken(payload.requestToken);
}

void (async () => {
  await initializeCsrfToken();

  const userApi = au.container.get(UserApi);
  const userContext = au.container.get(UserContext);
  const projectDraftStorageService = au.container.get(ProjectDraftStorageService);
  const linkToken = sessionStorage.getItem('linkToken');
  if (linkToken) {
    sessionStorage.removeItem('linkToken');
  }

  let deferredLoginError: string | null = null;
  let restoredEventId: string | null = null;

  // Hydrate auth state before the first route loads to avoid startup races where
  // route guards treat the user as logged out right after OAuth redirect.
  try {
    const user = await userApi.getCurrentUser(linkToken ?? undefined);
    if (user?.id) {
      userContext.setUserInfo(user);
      restoredEventId = projectDraftStorageService.tryRestoreDraft();
    }
  } catch (err: any) {
    if (err?.statusCode === 409) {
      // A different OAuth provider is trying to claim an email already owned by another provider.
      sessionStorage.setItem('login_error', 'provider_mismatch');
      window.location.href = '/auth/signout';
      return;
    }

    // 401 = not logged in; a deferred login error should be shown once router starts.
    deferredLoginError = sessionStorage.getItem('login_error');
    if (deferredLoginError) {
      sessionStorage.removeItem('login_error');
    }
  }

  // Rewrite the URL to the correct initial route before Aurelia starts.
  // All routing decisions live here; no post-startup router.load() calls needed.
  const startupParams = new URLSearchParams(window.location.search);
  const startupAction = startupParams.get('action');
  const startupCode = startupParams.get('code');
  const startupPath = startupParams.get('path');
  const base = window.location.pathname.replace(/\/?$/, '/');

  if (deferredLoginError) {
    history.replaceState(null, '', `${base}login?error=${deferredLoginError}`);
  } else if (userContext.isLoggedIn && userContext.userEntity.termsAcceptedAt == null && !window.location.pathname.includes('onboarding')) {
    const returnPath = restoredEventId ? `create/event-summary/${restoredEventId}` : null;
    const target = returnPath ? `onboarding?returnUrl=${encodeURIComponent(returnPath)}` : 'onboarding';
    history.replaceState(null, '', `${base}${target}`);
  } else {
    if (restoredEventId) {
      sessionStorage.setItem('restored_draft_event_id', restoredEventId);
    }
    if (startupAction === 'present' && startupCode) {
      history.replaceState(null, '', `${base}present/${startupCode}`);
    } else if (startupPath) {
      history.replaceState(null, '', `${base}${startupPath}`);
    }
  }

  await au
    .app({ host: document.querySelector('app'), component: Shell })
    .start();

  document.querySelector('app .splash')?.remove();
})();
